import type {
  CoinInventory,
  RoundResult,
  ShowMeTheCoinPhase,
  ShowMeTheCoinPlayerId,
} from "@cloudnest/show-me-the-coin-engine";
import { COIN_DENOMINATIONS } from "@cloudnest/show-me-the-coin-engine";

export interface CoinSubmissionView {
  readonly count: number;
  /** Denominations, or null when masked (opponent's un-revealed submission). */
  readonly coins: readonly number[] | null;
}

interface ShowMeTheCoinViewProps {
  readonly round: number;
  readonly phase: ShowMeTheCoinPhase;
  readonly leadPlayerId: ShowMeTheCoinPlayerId;
  readonly currentPlayerId: ShowMeTheCoinPlayerId;
  readonly chips: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  readonly pot: number;
  readonly contributed: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  readonly submission: Readonly<
    Record<ShowMeTheCoinPlayerId, CoinSubmissionView | null>
  >;
  /** null = masked (observer view). */
  readonly inventory: Readonly<
    Record<ShowMeTheCoinPlayerId, CoinInventory | null>
  >;
  readonly lastRound: RoundResult | null;
  readonly names: Readonly<Record<ShowMeTheCoinPlayerId, string>>;
}

const PHASE_LABEL: Record<ShowMeTheCoinPhase, string> = {
  "submit-lead": "선 제출",
  "submit-follow": "후 제출",
  bet: "베팅",
  "game-over": "종료",
};

function CoinChip({ denom }: { denom: number }) {
  return (
    <span
      data-testid="show-me-the-coin-coin"
      data-denom={denom}
      className="inline-flex items-center justify-center rounded-full font-bold text-[10px]"
      style={{
        width: 26,
        height: 26,
        color: "#1a1407",
        background: `var(--cn-show-me-the-coin-coin-${denom})`,
        boxShadow: "0 0 0 1.5px var(--cn-show-me-the-coin-gold-ring)",
      }}
    >
      {denom}
    </span>
  );
}

function MaskedCoin() {
  return (
    <span
      data-testid="show-me-the-coin-coin-masked"
      role="img"
      aria-label="가려진 코인"
      className="inline-flex items-center justify-center rounded-full font-bold text-white text-xs"
      style={{
        width: 26,
        height: 26,
        background: "var(--cn-show-me-the-coin-masked)",
        boxShadow: "0 0 0 1.5px var(--cn-show-me-the-coin-gold-ring)",
      }}
    >
      ?
    </span>
  );
}

function SubmissionTray({
  playerId,
  name,
  submission,
  contributed,
  isLead,
  isCurrent,
}: {
  playerId: ShowMeTheCoinPlayerId;
  name: string;
  submission: CoinSubmissionView | null;
  contributed: number;
  isLead: boolean;
  isCurrent: boolean;
}) {
  const count = submission?.count ?? 0;
  const coins = submission?.coins ?? null;
  return (
    <div
      data-testid={`show-me-the-coin-submission-${playerId}`}
      data-count={count}
      data-revealed={coins !== null ? "true" : "false"}
      className="flex flex-col gap-2 rounded-md border p-3"
      style={{
        borderColor: isCurrent
          ? "var(--cn-show-me-the-coin-gold-ring)"
          : "var(--cn-show-me-the-coin-border)",
        background: "var(--cn-show-me-the-coin-panel)",
      }}
    >
      <div className="flex items-center justify-between text-sm">
        <span
          className="font-semibold"
          style={{ color: `var(--cn-show-me-the-coin-${playerId})` }}
        >
          {name}
          {isLead ? " · 선" : ""}
        </span>
        <span style={{ color: "var(--cn-show-me-the-coin-text)" }}>
          제출 {count}개 · 베팅 {contributed}
        </span>
      </div>
      <div className="flex min-h-[28px] flex-wrap items-center gap-1">
        {submission === null ? (
          <span className="text-text-muted text-xs">미제출</span>
        ) : coins !== null ? (
          coins.map((denom, index) => (
            <CoinChip key={`${denom}-${index}`} denom={denom} />
          ))
        ) : (
          Array.from({ length: count }, (_unused, index) => (
            <MaskedCoin key={index} />
          ))
        )}
      </div>
    </div>
  );
}

function InventoryRow({
  playerId,
  name,
  inventory,
}: {
  playerId: ShowMeTheCoinPlayerId;
  name: string;
  inventory: CoinInventory;
}) {
  return (
    <div
      data-testid={`show-me-the-coin-inventory-${playerId}`}
      className="flex items-center justify-between text-xs"
      style={{ color: "var(--cn-show-me-the-coin-text)" }}
    >
      <span style={{ color: `var(--cn-show-me-the-coin-${playerId})` }}>
        {name}
      </span>
      <span className="flex gap-2">
        {COIN_DENOMINATIONS.map((denom) => (
          <span key={denom} data-denom={denom}>
            {denom}×{inventory[denom]}
          </span>
        ))}
      </span>
    </div>
  );
}

/**
 * Read-only match panel for 쇼 미 더 코인. Renders chips, pot, both submissions
 * (count always; denominations only when revealed), and — when revealed (recap)
 * — both coin inventories. The opponent's coin values stay hidden in the default
 * observer view. Pure presentation, driven by props from the replay screen.
 */
export function ShowMeTheCoinView({
  round,
  phase,
  leadPlayerId,
  currentPlayerId,
  chips,
  pot,
  contributed,
  submission,
  inventory,
  lastRound,
  names,
}: ShowMeTheCoinViewProps) {
  const showInventory = inventory.p1 !== null && inventory.p2 !== null;

  return (
    <div
      data-testid="show-me-the-coin-board"
      className="flex w-full flex-col gap-3 rounded-md p-3"
      style={{
        background: "var(--cn-show-me-the-coin-panel-alt)",
        maxWidth: "min(94vw, 560px)",
      }}
    >
      {/* Round / phase + chip & pot bar */}
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">라운드 {round + 1}</span>
        <span
          data-testid="show-me-the-coin-phase"
          className="rounded-full px-2 py-0.5 text-xs"
          style={{
            background: "var(--cn-show-me-the-coin-panel)",
            color: "var(--cn-show-me-the-coin-text)",
          }}
        >
          {PHASE_LABEL[phase]}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          data-testid="show-me-the-coin-chips-p1"
          className="font-semibold text-sm"
          style={{ color: "var(--cn-show-me-the-coin-p1)" }}
        >
          {names.p1} · 칩 {chips.p1}
        </span>
        <span
          data-testid="show-me-the-coin-pot"
          className="rounded-md px-3 py-1 font-bold text-sm text-white"
          style={{ background: "var(--cn-show-me-the-coin-pot)" }}
        >
          팟 {pot}
        </span>
        <span
          data-testid="show-me-the-coin-chips-p2"
          className="font-semibold text-sm"
          style={{ color: "var(--cn-show-me-the-coin-p2)" }}
        >
          {names.p2} · 칩 {chips.p2}
        </span>
      </div>

      {/* Submissions */}
      <div className="grid grid-cols-2 gap-2">
        {(["p1", "p2"] as const).map((playerId) => (
          <SubmissionTray
            key={playerId}
            playerId={playerId}
            name={names[playerId]}
            submission={submission[playerId]}
            contributed={contributed[playerId]}
            isLead={leadPlayerId === playerId}
            isCurrent={currentPlayerId === playerId && phase !== "game-over"}
          />
        ))}
      </div>

      {/* Last-round reveal (public) */}
      {lastRound && (
        <div
          data-testid="show-me-the-coin-last-round"
          className="rounded-md border px-3 py-2 text-center text-xs"
          style={{
            borderColor: "var(--cn-show-me-the-coin-border)",
            background: "var(--cn-show-me-the-coin-panel)",
            color: "var(--cn-show-me-the-coin-text)",
          }}
        >
          직전 라운드 {lastRound.round + 1}: {names.p1} {lastRound.values.p1} vs{" "}
          {names.p2} {lastRound.values.p2} ·{" "}
          {lastRound.folderId !== null ? (
            <span style={{ color: "var(--cn-show-me-the-coin-fold)" }}>
              {names[lastRound.folderId]} 폴드 →{" "}
            </span>
          ) : null}
          <span
            style={{
              color:
                lastRound.winnerId === "push"
                  ? "var(--cn-show-me-the-coin-text)"
                  : "var(--cn-show-me-the-coin-win)",
              fontWeight: 700,
            }}
          >
            {lastRound.winnerId === "push"
              ? "푸시"
              : `${names[lastRound.winnerId]} 라운드 승 (팟 ${lastRound.pot})`}
          </span>
        </div>
      )}

      {/* Inventories — only in the recap (revealed) view */}
      {showInventory && (
        <div
          data-testid="show-me-the-coin-inventory"
          className="flex flex-col gap-1 rounded-md border p-2"
          style={{
            borderColor: "var(--cn-show-me-the-coin-border)",
            background: "var(--cn-show-me-the-coin-panel)",
          }}
        >
          <div className="text-text-muted text-xs">보유 코인 (복기)</div>
          <InventoryRow
            playerId="p1"
            name={names.p1}
            inventory={inventory.p1 as CoinInventory}
          />
          <InventoryRow
            playerId="p2"
            name={names.p2}
            inventory={inventory.p2 as CoinInventory}
          />
        </div>
      )}
    </div>
  );
}
